import _, {keyBy} from 'lodash';
import memoize from 'lru-memoize';
import {Duration} from 'luxon';
import {createSelector} from 'reselect';

import {Dictionary} from '../../../libs/shared/helpers/src/typescript/typeHelpers';
import {buildCustomEmojiList} from '../../../srcCommon/helpers/emoji/customEmojiHelpers';
import {emojiToShortcode, emojiToUrl} from '../../../srcCommon/helpers/emoji/emojiHelpers';
import {SkinTonesEnum} from '../../models/api/preferencesModel';
import {
  baseEmojiList,
  buildEmojiValidationMap,
  EmojiDefinition,
  EmojiMap,
  isCustomEmoji,
} from '../../services/emojis/emojis';
import {getCustomEmojiShortcodes, getCustomEmojisRoute} from '../data/customEmojis/customEmojiSelectors';
import {getEmojiUsage} from '../data/localPreferences/localPreferencesSelectors';
import {getEmojiSkinTone} from '../data/preferences/preferencesSelectors';

/*
 * Usage.
 */

export const getEmojiList = createSelector(
  [getCustomEmojiShortcodes, getCustomEmojisRoute],
  (customEmojiShortcodeList, emojiRoute): EmojiDefinition[] =>
    baseEmojiList.concat(buildCustomEmojiList(customEmojiShortcodeList, emojiRoute)),
);

export const getEmojiMap = createSelector(
  getEmojiList,
  (emojiList): EmojiMap => keyBy(emojiList, 'shortcode'),
);

export const getEmojiValidationMap = createSelector(getEmojiMap, (emojiMap) =>
  buildEmojiValidationMap(emojiMap),
);

/* eslint no-param-reassign: 0 */
const getEmojiUsageScoresByShortcode = createSelector(getEmojiUsage, (usage) => {
  const now = Date.now();
  const scale = Duration.fromObject({days: 30}).valueOf();

  // Find the score for each emoji.
  return usage.reduce((result, use) => {
    // Find how much this use counts for.
    const contribution = Math.max(0, (scale - (now - use.date)) / scale);

    // Add it to the score for this emoji.
    const score = result[use.shortcode] || 0;
    result[use.shortcode] = score + contribution;

    return result;
  }, {} as {[T: string]: number});
});

export const getFrequentlyUsedEmoji = createSelector(
  [getEmojiMap, getEmojiUsageScoresByShortcode],
  (emojiMap, scores) =>
    _(scores)
      .keys()
      .sortBy((k) => -scores[k] || 0)
      .map((k) => emojiMap[k])
      .compact()
      .take(20)
      .value(),
);

export const getShouldShowCustomEmojiCategory = createSelector([getEmojiList], (emojiList) =>
  emojiList.some(isCustomEmoji),
);

/*
 * Search.
 */

export interface SearchEmojiDefinition {
  readonly emoji: EmojiDefinition;
  readonly skinTone?: SkinTonesEnum;
  readonly matchingShortcode: string;
  readonly fullShortcode: string;
  readonly imageUrl: string;
}

export type EmojiSearcher = (searchTerm: string, limit?: number) => ReadonlyArray<SearchEmojiDefinition>;

export const getEmojiSearcher = createSelector(
  [getEmojiList, getEmojiSkinTone, getEmojiUsageScoresByShortcode],
  (emojiList, skinTone, usageScores): EmojiSearcher =>
    memoize(20)((searchTerm: string, limit = 30) =>
      searchEmojiBase(emojiList, skinTone, usageScores, searchTerm, limit),
    ),
);

/** Search for emoji matching the provided search term. */
function searchEmojiBase(
  emojiList: ReadonlyArray<EmojiDefinition>,
  skinTone: SkinTonesEnum,
  usageScores: Dictionary<number>,
  searchTerm: string,
  limit: number,
): ReadonlyArray<SearchEmojiDefinition> {
  // Optimization for empty strings.
  if (!searchTerm.trim()) {
    return [];
  }

  return _(emojiList)
    .filter((emoji) => Boolean(findMatchingShortcode(emoji, searchTerm)))
    .take(limit)
    .map((emoji) => {
      // Find the matching shortcode.
      const matchingShortcode = findMatchingShortcode(emoji, searchTerm);
      if (!matchingShortcode) {
        return null;
      }

      // Build the search result.
      const result: SearchEmojiDefinition = {
        emoji,
        skinTone,
        matchingShortcode,
        fullShortcode: emojiToShortcode(emoji, skinTone),
        imageUrl: emojiToUrl(emoji, skinTone),
      };
      return result;
    })
    .compact()
    .sortBy(
      (s) => -(usageScores[s.emoji.shortcode] || 0),
      (s) => s.matchingShortcode,
    )
    .value();
}

function findMatchingShortcode(emoji: EmojiDefinition, searchTerm: string) {
  const lowerCaseSearchTerm = searchTerm.toLowerCase();

  return emoji.shortcodes.find(
    (s) => s.includes(lowerCaseSearchTerm) || s.replace(/[-_.]+/g, '').includes(lowerCaseSearchTerm),
  );
}
